import { VgApiResult, vgApiRequest } from "./vg-api.service";

/**
 * The clinic's product catalogue - what can be sold on an invoice alongside
 * services. Backed by Org_Product_Table via Read_All_Organisation_Products.
 */
export type Product = {
  productId: number;
  name: string;
  /** What it sells for, per unit. */
  salesPrice?: number;
  salesCurrency?: number;
  code?: string;
};

const num = (v: unknown) => (v != null ? Number(v) : undefined);

const pick = (o: Record<string, unknown>, camel: string) =>
  o[camel] ?? o[camel.charAt(0).toUpperCase() + camel.slice(1)];

/** Prices come back as a money object, not a scalar. */
function money(raw: unknown): { amount?: number; currency?: number } {
  if (raw == null) return {};
  if (typeof raw === "number") return { amount: raw };
  const o = raw as Record<string, unknown>;
  return {
    amount: num(pick(o, "monetaryAmount")),
    currency: num(pick(o, "monetaryCurrency")),
  };
}

export function mapProduct(raw: unknown): Product {
  const o = (raw ?? {}) as Record<string, unknown>;
  const sales = money(pick(o, "salesPrice"));
  return {
    productId: Number(pick(o, "productId") ?? 0),
    name: String(pick(o, "productName") ?? "Unnamed product"),
    salesPrice: sales.amount,
    salesCurrency: sales.currency,
    code: pick(o, "userProductCode")
      ? String(pick(o, "userProductCode"))
      : undefined,
  };
}

type ProductListResponse = {
  productList?: unknown[];
  ProductList?: unknown[];
  listOfProducts?: unknown[];
};

export async function readOrgProducts(
  accessToken: string,
  orgId: number
): Promise<VgApiResult<Product[]>> {
  const { data, error } = await vgApiRequest<ProductListResponse>(
    accessToken,
    "Read_All_Organisation_Products",
    { orgId }
  );
  if (error) return { data: null, error };

  const list =
    data?.productList ?? data?.ProductList ?? data?.listOfProducts ?? [];
  return { data: list.map(mapProduct), error: null };
}
